import React, { useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from "date-fns";
import { C1Text, C2Text, C4Text } from "../text/CustomText";
import CustomIcon from "../image/CustomIcon";
import styled from "styled-components";
import { SizedBox } from "../CommonBaseComponents";
import { Theme } from "../../Theme";

const StyledGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
`;

function CustomCaptionLabel(props) {
    const { onCaptionClick, children } = props;

    return (
        <C1Text
            cursor={"pointer"}
            style={{ alignContent: "center" }}
            onClick={onCaptionClick}
        >
            {children}
        </C1Text>
    );
}

function CustomPrev(props) {
    return (
        <CustomIcon
            iconName={"previous"}
            size={"38px"}
            cursor={"pointer"}
            onClick={() => {
                props.setCurrentMonth(undefined);
                props.onClick();
            }}
            className={"rdp-button_previous"}
        />
    );
}

function CustomNext(props) {
    return (
        <CustomIcon
            iconName={"next"}
            size={"38px"}
            cursor={"pointer"}
            onClick={() => {
                props.setCurrentMonth(undefined);
                props.onClick();
            }}
            className={"rdp-button_next"}
        />
    );
}

function CustomWeekday(props) {
    return (
        <th>
            <C4Text>{props["aria-label"].substring(0, 3)}</C4Text>
        </th>
    );
}

const css = `
    .rdp-day_button {
        font-family: 'Pretendard', sans-serif;
        font-size: 14px;
        font-weight: 400;
        color: #171A1C;
    }
    .rdp-range_middle .rdp-day_button {
        color: #6250FC;
    }
    .rdp-root {
        --rdp-accent-color: #6250FC;
    }
`;

function CalendarComponent({
    range,
    setRange,
    onCaptionClick,
    type,
    setType,
    onMonthSelect,
    selectedMonth,
    onYearSelect,
    selectedYear,
    mode = "range",
    selectedDate,
    onSelectDate,
    rangeAnchor,
    disabledMatcher,
}) {
    const minYear = new Date().getFullYear() - 11;
    const maxYear = new Date().getFullYear();

    const [currentMonth, setCurrentMonth] = useState(range?.from ?? selectedDate);
    const [currentYear, setCurrentYear] = useState(
        selectedMonth?.getFullYear() ?? range?.from?.getFullYear() ?? maxYear
    );

    function handleYearChange(year) {
        if (year >= minYear && year <= maxYear) {
            setCurrentYear(year);
        }
    }

    const months = [
        { label: "January", value: 0 },
        { label: "February", value: 1 },
        { label: "March", value: 2 },
        { label: "April", value: 3 },
        { label: "May", value: 4 },
        { label: "June", value: 5 },
        { label: "July", value: 6 },
        { label: "August", value: 7 },
        { label: "September", value: 8 },
        { label: "October", value: 9 },
        { label: "November", value: 10 },
        { label: "December", value: 11 },
    ];

    const arrayLength = maxYear - minYear + 1;

    const years = Array.from({ length: arrayLength }, (v, i) => {
        const yearValue = minYear + i;

        return {
            label: String(yearValue),
            value: yearValue,
        };
    });

    if (type === "month") {
        return (
            <>
                <style>{css}</style>
                <div className={"rdp-root"} data-nav-layout={"around"} style={{ width: "100%" }}>
                    <div className={"rdp-months"} style={{ width: "100%", maxWidth: "100%" }}>
                        <div className={"rdp-month"} style={{ width: "100%" }}>
                            <CustomIcon
                                iconName={"previous"}
                                size={"38px"}
                                cursor={"pointer"}
                                onClick={() => handleYearChange(currentYear - 1)}
                                className={"rdp-button_previous"}
                            />
                            <div className={"rdp-month_caption"}>
                                <C1Text
                                    cursor={"pointer"}
                                    style={{ alignContent: "center" }}
                                    onClick={onCaptionClick} // <--- buttonProps.onClick을 사용
                                >
                                    {currentYear}
                                </C1Text>
                            </div>
                            <CustomIcon
                                iconName={"next"}
                                size={"38px"}
                                cursor={"pointer"}
                                onClick={() => handleYearChange(currentYear + 1)}
                                className={"rdp-button_next"}
                            />
                            <StyledGrid>
                                {months.map((month) => {
                                    const isSelected =
                                        selectedMonth &&
                                        currentYear === selectedMonth.getFullYear() &&
                                        month.value === selectedMonth.getMonth();
                                    return (
                                        <SizedBox
                                            key={month.value}
                                            width={"100%"}
                                            height={"44px"}
                                            cursor={"pointer"}
                                            borderRadius={"10px"}
                                            boxShadow={
                                                isSelected
                                                    ? "inset 0 0 0 1px #6250FC, 0 0 0 2px #6250FC1A"
                                                    : undefined
                                            }
                                            onClick={() => {
                                                const selected = new Date(currentYear, month.value, 1);
                                                if (onMonthSelect) {
                                                    onMonthSelect(selected);
                                                } else {
                                                    setCurrentMonth(selected);
                                                    setType("day");
                                                }
                                            }}
                                        >
                                            <C2Text
                                                fontColor={isSelected ? Theme.color.zinc850 : Theme.color.gray500}
                                                fontWeight={isSelected ? "600" : "400"}
                                            >
                                                {month.label}
                                            </C2Text>
                                        </SizedBox>
                                    );
                                })}
                            </StyledGrid>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (type === "year") {
        return (
            <>
                <style>{css}</style>
                <div className={"rdp-root"} data-nav-layout={"around"} style={{ width: "100%" }}>
                    <div className={"rdp-months"} style={{ width: "100%", maxWidth: "100%" }}>
                        <div className={"rdp-month"} style={{ width: "100%" }}>
                            <CustomIcon
                                iconName={"previous"}
                                size={"38px"}
                                cursor={"pointer"}
                                onClick={() => { }}
                                className={"rdp-button_previous"}
                            />
                            <div className={"rdp-month_caption"}>
                                <C1Text cursor={"pointer"} style={{ alignContent: "center" }}>
                                    {minYear} - {maxYear}
                                </C1Text>
                            </div>
                            <CustomIcon
                                iconName={"next"}
                                size={"38px"}
                                cursor={"pointer"}
                                onClick={() => { }}
                                className={"rdp-button_next"}
                            />
                            <StyledGrid>
                                {years.map((year) => {
                                    const isSelected = onYearSelect
                                        ? selectedYear === year.value
                                        : selectedMonth && year.value === selectedMonth.getFullYear();
                                    return (
                                        <SizedBox
                                            key={year.value}
                                            width={"100%"}
                                            height={"44px"}
                                            cursor={"pointer"}
                                            borderRadius={"10px"}
                                            boxShadow={
                                                isSelected
                                                    ? "inset 0 0 0 1px #6250FC, 0 0 0 2px #6250FC1A"
                                                    : undefined
                                            }
                                            onClick={() => {
                                                if (onYearSelect) {
                                                    onYearSelect(year.value);
                                                } else {
                                                    handleYearChange(year.value);
                                                    setType("month");
                                                }
                                            }}
                                        >
                                            <C2Text
                                                fontColor={isSelected ? Theme.color.zinc850 : Theme.color.gray500}
                                                fontWeight={isSelected ? "600" : "400"}
                                            >
                                                {year.label}
                                            </C2Text>
                                        </SizedBox>
                                    );
                                })}
                            </StyledGrid>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const isSingle = mode === "single" || mode === "week";

    let fillRight = null; // 끝점: 오른쪽 절반만 연보라 (왼쪽은 흰색) — 밴드 시작점
    let fillLeft = null; // 끝점: 왼쪽 절반만 연보라 (오른쪽은 흰색) — 밴드 끝점

    // week 모드: 선택 주 전체 연보라. 단, 경계일(원형)은 바깥쪽 절반을 흰색으로 트리밍
    let weekDays = null;
    if (mode === "week" && selectedDate) {
        const ws = startOfWeek(selectedDate);
        const we = endOfWeek(selectedDate);
        const allWeek = eachDayOfInterval({ start: ws, end: we });
        if (isSameDay(selectedDate, ws)) {
            // 시작주의 시작일(일): 왼쪽 흰색, 오른쪽부터 밴드
            fillRight = [selectedDate];
            weekDays = allWeek.filter((d) => !isSameDay(d, selectedDate));
        } else if (isSameDay(selectedDate, we)) {
            // 종료주의 종료일(토): 오른쪽 흰색, 왼쪽까지 밴드
            fillLeft = [selectedDate];
            weekDays = allWeek.filter((d) => !isSameDay(d, selectedDate));
        } else {
            weekDays = allWeek;
        }
    }

    // 시작↔종료 연결 표시 (day 모드): rangeAnchor(반대편 확정일) ~ selectedDate 사이를 연보라로 연결
    let rangeMiddle = null;
    let anchorDays = null;
    if (isSingle && rangeAnchor && selectedDate) {
        const lo = rangeAnchor < selectedDate ? rangeAnchor : selectedDate;
        const hi = rangeAnchor < selectedDate ? selectedDate : rangeAnchor;
        rangeMiddle = eachDayOfInterval({ start: lo, end: hi }).slice(1, -1);
        anchorDays = [rangeAnchor];
        // 사이 기간(중간일)이 있을 때만 끝점 셀의 연결 방향 절반을 채움
        if (rangeMiddle.length) {
            fillRight = [lo];
            fillLeft = [hi];
        }
    }

    const modifiers = {};
    const modifiersClassNames = {};
    if (weekDays && weekDays.length) {
        modifiers.week = weekDays;
        modifiersClassNames.week = "rdp-week-highlight";
    }
    if (rangeMiddle && rangeMiddle.length) {
        modifiers.rangeMid = rangeMiddle;
        modifiersClassNames.rangeMid = "rdp-range-mid";
    }
    if (anchorDays) {
        modifiers.rangeAnchor = anchorDays;
        modifiersClassNames.rangeAnchor = "rdp-range-anchor";
    }
    if (fillRight) {
        modifiers.rangeFillRight = fillRight;
        modifiersClassNames.rangeFillRight = "rdp-range-fill-right";
    }
    if (fillLeft) {
        modifiers.rangeFillLeft = fillLeft;
        modifiersClassNames.rangeFillLeft = "rdp-range-fill-left";
    }
    const hasModifiers = Object.keys(modifiers).length > 0;

    const selectionProps = isSingle
        ? { mode: "single", selected: selectedDate, onSelect: (day) => day && onSelectDate?.(day) }
        : { mode: "range", selected: range, onSelect: setRange };

    return (
        <div>
            <style>{css}</style>
            <DayPicker
                {...selectionProps}
                disabled={disabledMatcher}
                month={currentMonth}
                numberOfMonths={1}
                navLayout="around"
                modifiers={hasModifiers ? modifiers : undefined}
                modifiersClassNames={hasModifiers ? modifiersClassNames : undefined}
                components={{
                    CaptionLabel: (props) => <CustomCaptionLabel {...props} onCaptionClick={onCaptionClick} />,
                    // PreviousMonthButton: CustomPrev,
                    PreviousMonthButton: (props) => <CustomPrev {...props} setCurrentMonth={setCurrentMonth} />,
                    // NextMonthButton: CustomNext,
                    NextMonthButton: (props) => <CustomNext {...props} setCurrentMonth={setCurrentMonth} />,
                    Weekday: CustomWeekday,
                    // Day: CustomDay,
                }}
            />
        </div>
    );
}

export default CalendarComponent;
